/**
 * Dominio de Continuidad Cinematográfica e Inspector de Raccord (Fase 2B.3B).
 *
 * Taxonomía estricta de 4 categorías:
 * 1. REAL_CONTRADICTION: Niega directamente hechos canónicos confirmados o condiciones físicas contiguas sin elipsis.
 * 2. POSSIBLE_RISK: Transición arriesgada pero plausible (ej. cruce de eje 180°).
 * 3. MISSING_INFORMATION: Parámetros de cámara o iluminación sin especificar (permanece desconocida; nunca asumir estática).
 * 4. REGISTERED_UNUSED_ASSET: Asset/personaje canónico no presente en la toma (NUNCA califica como contradicción).
 *
 * Reglas de seguridad:
 * - Identificadores estables y deterministas para evitar duplicados.
 * - No modifica escenas automáticamente; cualquier propuesta requiere aprobación explícita.
 * - hasVisualAnalysis es estrictamente false (análisis textual, sin visión artificial de píxeles).
 */

export const CONTINUITY_RISK_TYPES = {
  REAL_CONTRADICTION: "real_contradiction",
  POSSIBLE_RISK: "possible_risk",
  MISSING_INFORMATION: "missing_information",
  REGISTERED_UNUSED_ASSET: "registered_unused_asset",
};

export const RISK_TYPE_CONFIG = {
  [CONTINUITY_RISK_TYPES.REAL_CONTRADICTION]: {
    id: CONTINUITY_RISK_TYPES.REAL_CONTRADICTION,
    label: "Contradicción real",
    badgeClass: "border-red-500/40 bg-red-500/10 text-red-400",
    dotClass: "bg-red-500",
    severity: "conflict",
    description: "Niega directamente hechos canónicos o condiciones inmediatas sin elipsis temporal.",
  },
  [CONTINUITY_RISK_TYPES.POSSIBLE_RISK]: {
    id: CONTINUITY_RISK_TYPES.POSSIBLE_RISK,
    label: "Posible riesgo",
    badgeClass: "border-amber-500/40 bg-amber-500/10 text-amber-400",
    dotClass: "bg-amber-500",
    severity: "warning",
    description: "Transición cinematográfica arriesgada o cambio brusco en el eje de acción.",
  },
  [CONTINUITY_RISK_TYPES.MISSING_INFORMATION]: {
    id: CONTINUITY_RISK_TYPES.MISSING_INFORMATION,
    label: "Información faltante",
    badgeClass: "border-sky-500/40 bg-sky-500/10 text-sky-300",
    dotClass: "bg-sky-400",
    severity: "info",
    description: "Parámetros de cámara o entorno desconocidos. No se asume cámara fija ni estática.",
  },
  [CONTINUITY_RISK_TYPES.REGISTERED_UNUSED_ASSET]: {
    id: CONTINUITY_RISK_TYPES.REGISTERED_UNUSED_ASSET,
    label: "Referencia no utilizada",
    badgeClass: "border-zinc-500/40 bg-zinc-500/10 text-zinc-400",
    dotClass: "bg-zinc-400",
    severity: "info",
    description: "Personaje o asset registrado en el canon que no participa en esta toma (no es un error).",
  },
};

const CONFLICTING_GARMENTS = [
  "esmoquin", "tuxedo", "traje blanco", "bañador", "traje de baño",
  "ropa deportiva", "armadura", "vestido rojo", "bata de hospital",
  "uniforme militar", "camisa hawaiana", "chaqueta de cuero", "mono de trabajo"
];

const TEMPORAL_ELLIPSIS_MARKERS = [
  "más tarde", "al día siguiente", "semanas después", "meses después",
  "salto temporal", "años después", "a la mañana siguiente", "esa noche", "horas después"
];

/**
 * Analizador determinista de continuidad en frontend.
 * Genera exactamente la misma estructura de observaciones e IDs estables que el backend.
 */
export function analyzeProjectContinuity({ scenes = [], characters = [] }) {
  const observations = [];
  const sortedScenes = [...scenes].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  if (sortedScenes.length === 0) return observations;

  const analyzedScenes = [];

  sortedScenes.forEach((scene, idx) => {
    const sceneId = scene.id || `scn_${idx}`;
    const order = (scene.order ?? idx) + 1;
    const prompt = (scene.prompt || "").trim();
    const promptLower = prompt.toLowerCase();
    const notes = (scene.notes || "").trim();
    const notesLower = notes.toLowerCase();
    const title = scene.title || `Toma #${order}`;

    // Marcador de elipsis
    const hasTemporalEllipsis = TEMPORAL_ELLIPSIS_MARKERS.some((m) => promptLower.includes(m));

    // Análisis básico de clima y cámara
    const hasRain = promptLower.includes("lluvia") || promptLower.includes("tormenta") || promptLower.includes("nieve");
    const hasSun = promptLower.includes("soleado") || promptLower.includes("despejado") || promptLower.includes("sol") || promptLower.includes("pleno sol");
    const weather = hasRain ? "Lluvia persistente" : (hasSun ? "Despejado / Soleado" : null);

    // Contradicción documentada entre notas y prompt
    if (notes && !hasTemporalEllipsis) {
      const hasNotesRain = notesLower.includes("lluvia") || notesLower.includes("tormenta") || notesLower.includes("nieve");
      const hasPromptSun = promptLower.includes("soleado") || promptLower.includes("despejado") || promptLower.includes("sol") || promptLower.includes("pleno sol");
      if (hasNotesRain && hasPromptSun) {
        const obsId = `obs_${sceneId}_notes_weather_clash`;
        observations.push({
          id: obsId,
          aspect: "weather",
          riskType: CONTINUITY_RISK_TYPES.REAL_CONTRADICTION,
          severity: "conflict",
          title: "Contradicción documentada entre notas y prompt",
          description: `Las notas de continuidad de la toma exigen '${notes}', pero el prompt describe cielo soleado o despejado sin justificar el cambio.`,
          evidence: {
            canonSource: "Notas de continuidad de la toma",
            canonText: notes,
            sceneText: prompt,
            targetSceneId: sceneId,
            targetSceneOrder: order,
          },
          affectedSceneIds: [sceneId],
          recommendation: "Armoniza el prompt con las notas de continuidad de la toma o actualiza las notas si el cambio fue intencional.",
          analysisType: "text_script",
          hasVisualAnalysis: false,
          proposedFix: {
            type: "replace_prompt",
            sceneId,
            beforeText: prompt,
            afterText: `Bajo lluvia persistente, ${prompt}`,
            explanation: "Adapta el prompt para respetar las notas de continuidad de la toma.",
          },
        });
      }
    }

    const hasShotType = promptLower.includes("plano general") || promptLower.includes("wide shot") ||
      promptLower.includes("plano medio") || promptLower.includes("medium shot") ||
      promptLower.includes("plano medio-largo") || promptLower.includes("plano americano") ||
      promptLower.includes("plano entero") ||
      promptLower.includes("primer plano") || promptLower.includes("close-up") ||
      promptLower.includes("primerísimo plano") || promptLower.includes("plano cenital") ||
      promptLower.includes("plano detalle");

    const hasCameraMovement = promptLower.includes("travelling") || promptLower.includes("panorámica") ||
      promptLower.includes("panoramica") || promptLower.includes("paneo") ||
      promptLower.includes("cámara en mano") || promptLower.includes("drone") ||
      promptLower.includes("seguimiento") || promptLower.includes("seguimiento lateral") ||
      promptLower.includes("tracking") ||
      promptLower.includes("zoom") || promptLower.includes("estática") || promptLower.includes("estático") || promptLower.includes("fija");

    const screenDir = (promptLower.includes("izquierda a derecha") || promptLower.includes("izq a der"))
      ? "izq_der"
      : ((promptLower.includes("derecha a izquierda") || promptLower.includes("der a izq")) ? "der_izq" : null);

    analyzedScenes.push({
      id: sceneId,
      order,
      title,
      prompt,
      promptLower,
      weather,
      hasRain,
      hasSun,
      hasShotType,
      hasCameraMovement,
      screenDir,
    });

    // ----------------------------------------------------------------------
    // 1. PERSONAJES Y REFERENCIAS (CANON vs ESCENA)
    // ----------------------------------------------------------------------
    characters.forEach((char) => {
      const handle = char.handle || char.name || "personaje";
      const cleanHandle = `@${handle.replace(/[^\w]/g, "_").toLowerCase()}`;
      const bareHandle = cleanHandle.replace("@", "");
      const charName = char.name || cleanHandle;
      const desc = char.description || "";

      // Detección de vestuario base en la descripción
      const wardrobeMatch = desc.match(/(vestid[oa]|traje|chaqueta|ropa|mono|pantal[oó]n|uniforme|abrigo)[^.,;\n]*/i);
      const baseWardrobe = wardrobeMatch ? wardrobeMatch[0].trim() : null;

      // A. Comprobación de vinculación explícita con @handle
      const hasExplicitHandle = promptLower.includes(cleanHandle.toLowerCase());

      // B. Comprobación de mención textual sin handle (ej. 'azotea' vs '@azotea')
      let hasUnlinkedMention = false;
      let matchedWord = bareHandle;
      if (!hasExplicitHandle && bareHandle.length >= 3) {
        const regex = new RegExp(`\\b${bareHandle}\\b`, "i");
        const m = prompt.match(regex);
        if (m) {
          hasUnlinkedMention = true;
          matchedWord = m[0];
        }
      }

      if (hasExplicitHandle) {
        // Vinculada explícitamente: comprobar contradicciones de vestuario
        if (baseWardrobe && !hasTemporalEllipsis) {
          const baseWardrobeLower = baseWardrobe.toLowerCase();
          for (const garment of CONFLICTING_GARMENTS) {
            if (promptLower.includes(garment) && !baseWardrobeLower.includes(garment)) {
              const obsId = `obs_${sceneId}_wardrobe_conflict_${bareHandle}`;
              const proposedAfter = prompt.replace(new RegExp(garment, "gi"), baseWardrobe);
              observations.push({
                id: obsId,
                aspect: "wardrobe",
                riskType: CONTINUITY_RISK_TYPES.REAL_CONTRADICTION,
                severity: "conflict",
                title: `Contradicción de vestuario en ${charName}`,
                description: `El personaje ${charName} tiene fijado en canon '${baseWardrobe}', pero esta toma describe '${garment}' sin indicar elipsis temporal.`,
                evidence: {
                  canonSource: `Definición canónica de ${charName}`,
                  canonText: baseWardrobe,
                  sceneText: garment,
                  targetSceneId: sceneId,
                  targetSceneOrder: order,
                },
                affectedSceneIds: [sceneId],
                recommendation: `Verifica si deseas registrar un cambio de vestuario oficial, añadir un salto temporal o mantener su indumentaria canónica.`,
                analysisType: "text_script",
                hasVisualAnalysis: false,
                proposedFix: {
                  type: "replace_prompt",
                  sceneId,
                  beforeText: prompt,
                  afterText: proposedAfter,
                  explanation: `Sustituye '${garment}' por el vestuario canónico '${baseWardrobe}'.`,
                },
              });
              break;
            }
          }
        }
      } else if (hasUnlinkedMention) {
        // Posible coincidencia semántica mencionada en el texto sin handle
        const obsId = `obs_${sceneId}_unlinked_ref_${bareHandle}`;
        const proposedAfter = prompt.replace(new RegExp(`\\b${matchedWord}\\b`, "i"), cleanHandle);
        observations.push({
          id: obsId,
          aspect: "character",
          riskType: CONTINUITY_RISK_TYPES.POSSIBLE_RISK,
          severity: "warning",
          isUnlinkedRef: true,
          title: `Posible referencia sin vincular: ${charName} (${cleanHandle})`,
          description: `El texto de la toma menciona '${matchedWord}', coincidente con la referencia registrada '${charName}' (${cleanHandle}), pero no está vinculada formalmente con su etiqueta @.`,
          evidence: {
            canonSource: `Referencia registrada (${cleanHandle})`,
            sceneText: matchedWord,
            targetSceneId: sceneId,
            targetSceneOrder: order,
          },
          affectedSceneIds: [sceneId],
          recommendation: `Si esta toma debe emplear la referencia canónica '${charName}', vincúlala formalmente como ${cleanHandle} para garantizar el raccord visual, o descarta este aviso si es solo una mención genérica.`,
          analysisType: "text_script",
          hasVisualAnalysis: false,
          proposedFix: {
            type: "replace_prompt",
            sceneId,
            beforeText: prompt,
            afterText: proposedAfter,
            explanation: `Vincula formalmente la referencia sustituyendo '${matchedWord}' por '${cleanHandle}'.`,
          },
        });
      } else {
        // Asset/personaje canónico no presente
        const obsId = `obs_${sceneId}_unused_${bareHandle}`;
        observations.push({
          id: obsId,
          aspect: "character",
          riskType: CONTINUITY_RISK_TYPES.REGISTERED_UNUSED_ASSET,
          severity: "info",
          title: `Referencia no utilizada: ${charName}`,
          description: `El personaje o referencia '${charName}' (${cleanHandle}) está registrado en la biblia del proyecto, pero no participa en el texto de esta toma.`,
          evidence: {
            canonSource: `Ficha canónica (${cleanHandle})`,
            canonText: desc || "Sin descripción adicional",
            targetSceneId: sceneId,
            targetSceneOrder: order,
          },
          affectedSceneIds: [sceneId],
          recommendation: "Es habitual que los personajes o locaciones no aparezcan en todas las tomas. Esta observación es meramente informativa para control de reparto y no representa un error.",
          analysisType: "text_script",
          hasVisualAnalysis: false,
        });
      }
    });

    // ----------------------------------------------------------------------
    // 2. CONTINUIDAD ENTRE TOMAS CONTIGUAS (CLIMA Y EJE 180°)
    // ----------------------------------------------------------------------
    if (idx > 0) {
      const prev = analyzedScenes[idx - 1];

      // Salto climático sin elipsis
      if (!hasTemporalEllipsis && prev.hasRain && hasSun) {
        const obsId = `obs_${sceneId}_weather_clash_${prev.id}`;
        const proposedPrompt = `Bajo lluvia persistente, ${prompt}`;
        observations.push({
          id: obsId,
          aspect: "weather",
          riskType: CONTINUITY_RISK_TYPES.REAL_CONTRADICTION,
          severity: "conflict",
          title: "Ruptura brusca de meteorología sin elipsis",
          description: `La toma previa #${prev.order} fijó lluvia/tormenta, mientras que la toma #${order} describe cielo despejado o sol sin justificación temporal.`,
          evidence: {
            prevSceneText: prev.prompt,
            prevSceneOrder: prev.order,
            targetSceneText: prompt,
            targetSceneOrder: order,
            comparedSceneId: prev.id,
            targetSceneId: sceneId,
          },
          affectedSceneIds: [prev.id, sceneId],
          recommendation: "Conserva la misma meteorología para mantener el raccord de la secuencia o explicita un salto temporal.",
          analysisType: "text_script",
          hasVisualAnalysis: false,
          proposedFix: {
            type: "replace_prompt",
            sceneId,
            beforeText: prompt,
            afterText: proposedPrompt,
            explanation: `Adapta la toma #${order} para preservar la atmósfera de lluvia de la toma previa.`,
          },
        });
      }

      // Cruce de eje 180°
      if (prev.screenDir && screenDir && prev.screenDir !== screenDir) {
        const obsId = `obs_${sceneId}_axis_180_${prev.id}`;
        observations.push({
          id: obsId,
          aspect: "spatial_relation",
          riskType: CONTINUITY_RISK_TYPES.POSSIBLE_RISK,
          severity: "warning",
          title: "Posible cruce de eje espacial (Regla de 180°)",
          description: `La toma anterior #${prev.order} avanza de ${prev.screenDir === "izq_der" ? "izquierda a derecha" : "derecha a izquierda"} y la toma #${order} indica sentido opuesto sin curva ni plano neutro.`,
          evidence: {
            prevSceneText: prev.prompt,
            prevSceneOrder: prev.order,
            targetSceneText: prompt,
            targetSceneOrder: order,
            comparedSceneId: prev.id,
            targetSceneId: sceneId,
          },
          affectedSceneIds: [prev.id, sceneId],
          recommendation: "Intercala un plano frontal o describe un giro de trayectoria para evitar desorientar la mirada del espectador.",
          analysisType: "text_script",
          hasVisualAnalysis: false,
        });
      }
    }

    // ----------------------------------------------------------------------
    // 3. INFORMACIÓN FALTANTE DE CÁMARA
    // ----------------------------------------------------------------------
    if (!hasShotType && !hasCameraMovement) {
      const hasCameraIndications = promptLower.includes("cámara") || promptLower.includes("camara") ||
        promptLower.includes("altura de") || promptLower.includes("a la altura") ||
        promptLower.includes("seguimiento") || promptLower.includes("encuadre") ||
        promptLower.includes("ángulo") || promptLower.includes("angulo") ||
        promptLower.includes("perspectiva") || promptLower.includes("nivel de") || promptLower.includes("punto de vista");

      if (hasCameraIndications) {
        const obsId = `obs_${sceneId}_unstructured_camera`;
        observations.push({
          id: obsId,
          aspect: "camera_transition",
          riskType: CONTINUITY_RISK_TYPES.MISSING_INFORMATION,
          severity: "info",
          title: "Sin parámetros estructurados de cámara",
          description: "Sin parámetros estructurados de cámara; el prompt contiene indicaciones. No se deducen ejes ni movimientos que el análisis no pueda fundamentar.",
          evidence: {
            sceneText: prompt || "(prompt vacío)",
            targetSceneId: sceneId,
            targetSceneOrder: order,
          },
          affectedSceneIds: [sceneId],
          recommendation: "El prompt incluye indicaciones en texto. Para mayor precisión generativa, puedes especificar un tipo de plano estándar si lo consideras necesario.",
          analysisType: "text_script",
          hasVisualAnalysis: false,
        });
      } else {
        const obsId = `obs_${sceneId}_missing_camera`;
        observations.push({
          id: obsId,
          aspect: "camera_transition",
          riskType: CONTINUITY_RISK_TYPES.MISSING_INFORMATION,
          severity: "info",
          title: "Encuadre y cámara no especificados",
          description: "El prompt no especifica tipo de plano ni movimiento de cámara. Permanece desconocida; no se asume cámara fija ni estática.",
          evidence: {
            sceneText: prompt || "(prompt vacío)",
            targetSceneId: sceneId,
            targetSceneOrder: order,
          },
          affectedSceneIds: [sceneId],
          recommendation: "Definir el encuadre (ej. 'Plano general', 'Plano medio') ayuda a coordinar la escala visual entre planos.",
          analysisType: "text_script",
          hasVisualAnalysis: false,
        });
      }
    }
  });

  // Regla: Con una sola toma, permitir comprobar coherencia interna y con el personaje,
  // aclarando que no puede verificarse continuidad ni raccord entre tomas.
  if (sortedScenes.length === 1) {
    const singleSc = sortedScenes[0];
    const singleScId = singleSc.id || "scn_0";
    const singleOrder = (singleSc.order ?? 0) + 1;
    const singlePrompt = (singleSc.prompt || "").trim();
    observations.push({
      id: `obs_${singleScId}_single_scene_scope`,
      aspect: "sequence",
      riskType: CONTINUITY_RISK_TYPES.MISSING_INFORMATION,
      severity: "info",
      title: "Toma única: limitación del análisis y revisión de coherencia interna",
      description: "El proyecto cuenta con una sola toma (limitación del análisis, no un error). Se ha evaluado la coherencia interna de su texto y personajes, pero no es posible verificar la continuidad de raccord temporal o espacial entre tomas consecutivas hasta incorporar más escenas al Storyboard.",
      evidence: {
        sceneText: singlePrompt || "(prompt vacío)",
        targetSceneId: singleScId,
        targetSceneOrder: singleOrder,
      },
      affectedSceneIds: [singleScId],
      recommendation: "Añade tomas consecutivas al Storyboard cuando desees evaluar la coherencia de raccord entre planos consecutivos.",
      analysisType: "text_script",
      hasVisualAnalysis: false,
    });
  }

  return observations;
}

/**
 * Filtra observaciones pendientes para una escena específica, excluyendo las ya descartadas o revisadas.
 */
export function getPendingSceneObservations(sceneId, observations = [], decisions = {}) {
  return observations.filter((obs) => {
    const affectsScene = obs.affectedSceneIds?.includes(sceneId);
    if (!affectsScene) return false;
    const dec = decisions[obs.id];
    // Pendiente si no tiene decisión o status es 'pending'
    return !dec || dec.status === "pending";
  });
}

/**
 * Determina la severidad dominante entre las observaciones pendientes de una escena.
 * Retorna: "conflict" | "warning" | "info" | null
 */
export function getSceneSeverity(pendingObservations = []) {
  if (pendingObservations.length === 0) return null;
  if (pendingObservations.some((o) => o.severity === "conflict" || o.riskType === CONTINUITY_RISK_TYPES.REAL_CONTRADICTION)) {
    return "conflict";
  }
  if (pendingObservations.some((o) => o.severity === "warning" || o.riskType === CONTINUITY_RISK_TYPES.POSSIBLE_RISK)) {
    return "warning";
  }
  return "info";
}

/**
 * Determina si una observación puede abordarse mediante corrección del prompt de una toma.
 * No es aplicable para referencias no utilizadas, aviso de toma única ni información faltante de cámara o estructura.
 */
export function isObservationFixable(obs) {
  if (!obs) return false;
  const riskType = obs.risk_type || obs.riskType;
  if (
    riskType === CONTINUITY_RISK_TYPES.REGISTERED_UNUSED_ASSET ||
    riskType === CONTINUITY_RISK_TYPES.MISSING_INFORMATION ||
    obs.id?.includes("single_scene_scope") ||
    obs.id?.includes("unused") ||
    obs.id?.includes("missing_camera") ||
    obs.id?.includes("unstructured_camera")
  ) {
    return false;
  }
  const affected = obs.affected_scene_ids || obs.affectedSceneIds || [];
  if (!Array.isArray(affected) || affected.length === 0) return false;

  return (
    riskType === CONTINUITY_RISK_TYPES.REAL_CONTRADICTION ||
    riskType === CONTINUITY_RISK_TYPES.POSSIBLE_RISK ||
    Boolean(obs.proposed_fix || obs.proposedFix)
  );
}

